<script>
  import { Modal } from "svelte-utilities";
  import { onMount } from "svelte";

  import CaretLeftIcon from "@local/assets/icons/caret-left.svg";

  import NewSourceDocument from "./NewSourceDocument.svelte";
  import NewWorkingDocument from "./NewWorkingDocument.svelte";
  import NewRemoteLink from "./NewRemoteLink.svelte";

  import eb from "#src/extensions/event-bus.js";

  /**
   * @typedef {"imported" | "virtual" | "remote" | null} DocumentType
   */

  function createDoc(groupId, index) {
    return {
      id: crypto.randomUUID(),
      group_id: groupId,
      index,
      name: "Untitled",
      ready: true,
      data: {
        pages: null,
        thumbnail: {},
      },
      is_virtual: true,
      /** @type {DocumentType} */
      type: null,
      source_document_id: null,
    };
  }

  export let documents;
  export let group;

  let modal;
  let newDoc = createDoc();
  let firstRun = false;

  function openModal(e) {
    newDoc = createDoc($group.id, documents.length);
    firstRun = false;
    if (!documents.length && e?.firstRun) {
      if (e?.firstRun) {
        firstRun = true;
      }
      chooseType("imported");
    }
    modal.open();
  }

  function chooseType(type) {
    newDoc.type = type;

    if (type === "virtual") {
      const first = documents.find((d) => d.type === "imported");
      if (first) newDoc.source_document_id = first.id;
    } else if (type === "remote") {
      newDoc.data.doctype = null;
      newDoc.data.url = "";
    }
  }

  function close() {
    if (modal) {
      modal.close();
    }
  }

  onMount(() => {
    eb.on("open-create-document-modal", openModal);

    return () => {
      eb.unsubscribe("open-create-document-modal", openModal);
    };
  });
</script>

<Modal bind:this={modal} closeable width="36rem">
  <div slot="title">Create New Document</div>
  <div slot="content" class="flex flex-col gap-4 overflow-hidden">
    {#if !newDoc.type}
      <div>Document Type:</div>
      <div class="doc-button-grid">
        <button on:click={() => chooseType("imported")}>
          <div class="font-bold">Source Document</div>
          <div class="text-xs description">Upload PDFs or Images</div>
        </button>
        {#if documents.some((d) => d.type === "imported")}
          <button on:click={() => chooseType("virtual")}>
            <div class="font-bold">Working Document</div>
            <div class="text-xs description">Choose a subset of an existing document</div>
          </button>
        {/if}
        <button on:click={() => chooseType("remote")}>
          <div class="font-bold">Remote Link</div>
          <div class="text-xs description">Link to an external URL</div>
        </button>
      </div>
    {:else if newDoc.type === "imported"}
      {#if !firstRun}
        <div>
          <button class="flex items-center gap-2 btn-text" on:click={() => (newDoc.type = null)}>
            <CaretLeftIcon />
            <div>Back</div>
          </button>
        </div>
      {/if}
      <NewSourceDocument {group} {documents} on:done-uploading={close} />
    {:else if newDoc.type === "virtual"}
      <div>
        <button class="flex items-center gap-2 btn-text" on:click={() => (newDoc.type = null)}>
          <CaretLeftIcon />
          <div>Back</div>
        </button>
      </div>
      <NewWorkingDocument {group} {documents} {newDoc} on:done-uploading={close} />
    {:else if newDoc.type === "remote"}
      <div>
        <button class="flex items-center gap-2 btn-text" on:click={() => (newDoc.type = null)}>
          <CaretLeftIcon />
          <div>Back</div>
        </button>
      </div>
      <NewRemoteLink {group} {newDoc} on:done-uploading={close} />
    {/if}
  </div>
</Modal>

<style lang="scss">
  .doc-button-grid {
    @apply flex gap-4;

    button {
      @apply border bg-gray-200 border-gray-300 basis-1/2 p-4 flex flex-col justify-between gap-4;

      &:hover {
        @apply border-gray-500;
      }
    }

    button .description {
      @apply flex flex-col justify-center h-8;
    }
  }
</style>
